import { useEffect } from 'react';
import { useExecutionsStore } from '../store/executionsStore';
import {
  resolveNeoAttackTraceSync,
  type BackendTraceStatus,
} from '../utils/neoAttackStatus';

type NeoAttackTraceLike = {
  name: string;
  status: BackendTraceStatus;
  threadId: string;
  createdAt: string | Date;
};

export function useSyncNeoAttackFromTraces({
  signalId,
  applicationId,
  traces,
}: {
  signalId?: string;
  applicationId?: string;
  traces?: readonly NeoAttackTraceLike[];
}) {
  const neoAttackState = useExecutionsStore((state) =>
    signalId ? state.getNeoAttackState(signalId) : undefined,
  );
  const addNeoAttack = useExecutionsStore((state) => state.addNeoAttack);
  const updateNeoAttackStatus = useExecutionsStore(
    (state) => state.updateNeoAttackStatus,
  );

  useEffect(() => {
    if (!traces || !signalId || !applicationId) {
      return;
    }

    const neoTrace = traces
      .filter((trace) => trace.name === 'neo')
      .sort(
        (left, right) =>
          new Date(right.createdAt).getTime() -
          new Date(left.createdAt).getTime(),
      )[0];

    if (!neoTrace) {
      return;
    }

    const sync = resolveNeoAttackTraceSync({
      traceStatus: neoTrace.status,
      traceThreadId: neoTrace.threadId,
      traceCreatedAtMs: new Date(neoTrace.createdAt).getTime(),
      current: neoAttackState
        ? {
            status: neoAttackState.status,
            threadId: neoAttackState.threadId,
            startedAtMs: new Date(neoAttackState.startedAt).getTime(),
          }
        : null,
    });

    if (sync.kind === 'initialize' || sync.kind === 'adopt-running') {
      addNeoAttack({
        signalId,
        threadId: neoTrace.threadId,
        executionId: null,
        applicationId,
      });

      return;
    }

    if (sync.kind === 'adopt-terminal') {
      updateNeoAttackStatus({
        signalId,
        status: sync.status,
        threadId: neoTrace.threadId,
      });

      return;
    }

    if (sync.kind === 'update') {
      updateNeoAttackStatus({ signalId, status: sync.status });
    }
  }, [
    traces,
    signalId,
    applicationId,
    neoAttackState,
    addNeoAttack,
    updateNeoAttackStatus,
  ]);
}
